/**
 * 📥 IMPORTAÇÃO EXCEL - Gains Monitor
 * Importa Kaizens em lote a partir de arquivo Excel
 */

import { GainsMonitorService } from './gainsMonitorService';

interface KaizenImportRow {
  titulo: string;
  descricao?: string;
  tipo: string;
  responsavel: string;
  equipe?: string;
  data_planejada: string;
  data_alvo: string;
  baseline_lead_time: number;
  target_lead_time: number;
  baseline_cycle_time?: number;
  target_cycle_time?: number;
  baseline_ftr?: number;
  target_ftr?: number;
  baseline_capacity_fte?: number;
  target_capacity_fte?: number;
  baseline_cost?: number;
  target_cost?: number;
}

interface ImportResult {
  success: boolean;
  totalRows: number;
  importedCount: number;
  errors: string[];
  warnings: string[];
}

const VALID_TYPES = [
  'reducao_lead_time',
  'eliminacao_desperdicio',
  'automacao',
  'padronizacao',
  'qualidade',
  'ergonomia'
];

/**
 * Valida uma linha do Excel
 */
function validateRow(row: any, rowIndex: number): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Campos obrigatórios
  if (!row.titulo || row.titulo.trim() === '') {
    errors.push(`Linha ${rowIndex}: Campo "titulo" é obrigatório`);
  }

  if (!row.tipo || !VALID_TYPES.includes(row.tipo)) {
    errors.push(`Linha ${rowIndex}: Campo "tipo" inválido. Use: ${VALID_TYPES.join(', ')}`);
  }

  if (!row.responsavel || row.responsavel.trim() === '') {
    errors.push(`Linha ${rowIndex}: Campo "responsavel" é obrigatório`);
  }

  if (!row.data_planejada) {
    errors.push(`Linha ${rowIndex}: Campo "data_planejada" é obrigatório`);
  }

  if (!row.data_alvo) {
    errors.push(`Linha ${rowIndex}: Campo "data_alvo" é obrigatório`);
  }

  if (!row.baseline_lead_time || isNaN(Number(row.baseline_lead_time))) {
    errors.push(`Linha ${rowIndex}: Campo "baseline_lead_time" deve ser um número`);
  }

  if (!row.target_lead_time || isNaN(Number(row.target_lead_time))) {
    errors.push(`Linha ${rowIndex}: Campo "target_lead_time" deve ser um número`);
  }

  // Validação lógica
  if (Number(row.target_lead_time) >= Number(row.baseline_lead_time)) {
    errors.push(`Linha ${rowIndex}: "target_lead_time" deve ser menor que "baseline_lead_time"`);
  }

  // Validar FTR
  if (row.baseline_ftr !== undefined && (Number(row.baseline_ftr) < 0 || Number(row.baseline_ftr) > 100)) {
    errors.push(`Linha ${rowIndex}: "baseline_ftr" deve estar entre 0 e 100`);
  }

  if (row.target_ftr !== undefined && (Number(row.target_ftr) < 0 || Number(row.target_ftr) > 100)) {
    errors.push(`Linha ${rowIndex}: "target_ftr" deve estar entre 0 e 100`);
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Converte linha do Excel para objeto Kaizen
 */
function convertRowToKaizen(row: KaizenImportRow, organizationId?: string): any {
  return {
    title: row.titulo.trim(),
    description: row.descricao?.trim() || '',
    type: row.tipo,
    responsible: row.responsavel.trim(),
    team_members: row.equipe ? row.equipe.split(',').map(m => m.trim()) : [],
    planned_date: new Date(row.data_planejada).toISOString(),
    target_date: new Date(row.data_alvo).toISOString(),
    baseline_lead_time: Number(row.baseline_lead_time),
    target_lead_time: Number(row.target_lead_time),
    baseline_cycle_time: row.baseline_cycle_time !== undefined ? Number(row.baseline_cycle_time) : 0,
    target_cycle_time: row.target_cycle_time !== undefined ? Number(row.target_cycle_time) : 0,
    baseline_ftr: row.baseline_ftr !== undefined ? Number(row.baseline_ftr) : 100,
    target_ftr: row.target_ftr !== undefined ? Number(row.target_ftr) : 100,
    baseline_capacity_fte: row.baseline_capacity_fte !== undefined ? Number(row.baseline_capacity_fte) : 0,
    target_capacity_fte: row.target_capacity_fte !== undefined ? Number(row.target_capacity_fte) : 0,
    baseline_cost: row.baseline_cost !== undefined ? Number(row.baseline_cost) : 0,
    target_cost: row.target_cost !== undefined ? Number(row.target_cost) : 0,
    organization_id: organizationId || undefined,
    project_id: undefined,
    workflow_id: undefined,
  };
}

/**
 * 📥 Importa Kaizens a partir de arquivo Excel
 */
export async function importKaizensFromExcel(
  file: File,
  organizationId?: string
): Promise<ImportResult> {
  const result: ImportResult = {
    success: false,
    totalRows: 0,
    importedCount: 0,
    errors: [],
    warnings: [],
  };

  try {
    const XLSX = await import('xlsx');
    const { read, utils } = XLSX;

    // Ler arquivo Excel
    const buffer = await file.arrayBuffer();
    const workbook = read(buffer, { type: 'array' });

    // Verificar se existe a aba "Kaizens"
    if (!workbook.SheetNames.includes('Kaizens')) {
      result.errors.push('Aba "Kaizens" não encontrada no arquivo');
      return result;
    }

    // Ler dados da aba "Kaizens"
    const worksheet = workbook.Sheets['Kaizens'];
    const rows: KaizenImportRow[] = utils.sheet_to_json(worksheet);

    result.totalRows = rows.length;

    if (rows.length === 0) {
      result.errors.push('Nenhum dado encontrado na aba "Kaizens"');
      return result;
    }

    // Validar e importar cada linha
    for (let i = 0; i < rows.length; i++) {
      const rowIndex = i + 2; // +2 porque linha 1 é cabeçalho e Excel começa em 1
      const row = rows[i];

      // Validar linha
      const validation = validateRow(row, rowIndex);
      if (!validation.valid) {
        result.errors.push(...validation.errors);
        continue;
      }

      // Converter para formato Kaizen
      const kaizenData = convertRowToKaizen(row, organizationId);

      // Criar Kaizen via service
      try {
        await GainsMonitorService.createKaizen(kaizenData);
        result.importedCount++;
      } catch (error: any) {
        result.errors.push(`Linha ${rowIndex}: Erro ao criar Kaizen - ${error.message}`);
      }
    }

    result.success = result.importedCount > 0;

    // Adicionar warnings se houver falhas parciais
    if (result.importedCount > 0 && result.errors.length > 0) {
      result.warnings.push(`${result.importedCount} de ${result.totalRows} Kaizens importados com sucesso`);
    } else if (result.importedCount === result.totalRows) {
      result.warnings.push(`Todos os ${result.totalRows} Kaizens foram importados com sucesso!`);
    }

    return result;
  } catch (error: any) {
    result.errors.push(`Erro ao processar arquivo: ${error.message}`);
    return result;
  }
}

export const GainsImportService = {
  importKaizensFromExcel,
};

export default GainsImportService;
